import { supabase } from './client';
import { getCurrentEmpresaId } from './auth-utils';
import { getTodayAsISO } from '@/utils/dateOnly';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const contasPagarTable = () => supabase.from('contas_pagar' as any);

export interface ContaPagar {
  id?: string;
  empresa_id?: string;
  fornecedor_id?: string;
  descricao: string;
  categoria?: string;
  valor_total: number;
  valor_pago: number;
  data_vencimento: string;
  data_pagamento?: string;
  status: 'pendente' | 'pago' | 'atrasado' | 'cancelado';
  forma_pagamento?: string;
  observacoes?: string;
  pedido_compra_id?: string;
  created_at?: string;
  updated_at?: string;
  recorrencia?: 'unica' | 'diaria' | 'semanal' | 'mensal' | 'anual';
  is_ativa?: boolean;
  proxima_execucao?: string;
  recorrencia_grupo_id?: string;
}

export type RecorrenciaContaPagar = NonNullable<ContaPagar['recorrencia']>;

const normalizarData = (data: string) => data.split('T')[0];

const criarData = (data: string) => new Date(`${normalizarData(data)}T00:00:00`);

const formatarData = (data: Date) => data.toISOString().split('T')[0];

export const calcularProximaExecucao = (
  dataBase: string,
  recorrencia: RecorrenciaContaPagar
): string | null => {
  if (!dataBase || recorrencia === 'unica') {
    return null;
  }

  const data = criarData(dataBase);

  switch (recorrencia) {
    case 'diaria':
      data.setDate(data.getDate() + 1);
      break;
    case 'semanal':
      data.setDate(data.getDate() + 7);
      break;
    case 'mensal':
      data.setMonth(data.getMonth() + 1);
      break;
    case 'anual':
      data.setFullYear(data.getFullYear() + 1);
      break;
    default:
      return null;
  }

  return formatarData(data);
};

const gerarNovaContaRecorrente = (conta: ContaPagar, dataVencimento: string) => ({
  descricao: conta.descricao,
  categoria: conta.categoria,
  valor_total: conta.valor_total,
  valor_pago: 0,
  data_vencimento: dataVencimento,
  status: 'pendente' as const,
  forma_pagamento: conta.forma_pagamento,
  observacoes: conta.observacoes,
  fornecedor_id: conta.fornecedor_id,
  recorrencia: 'unica' as const,
  is_ativa: false,
  proxima_execucao: null,
  recorrencia_grupo_id: conta.recorrencia_grupo_id
});

const gerarChaveRecorrencia = (conta: ContaPagar, data: string) => {
  if (!conta.recorrencia_grupo_id) {
    return null;
  }

  return `${conta.recorrencia_grupo_id}-${data}`;
};

const processarRecorrenciasContas = async (contas: ContaPagar[], empresaId: string) => {
  const hoje = getTodayAsISO();
  const chavesExistentes = new Set(
    contas
      .map(conta => {
        if (!conta.recorrencia_grupo_id) {
          return null;
        }
        return gerarChaveRecorrencia(conta, normalizarData(conta.data_vencimento));
      })
      .filter(Boolean) as string[]
  );

  const novasContas: ContaPagar[] = [];

  for (const conta of contas) {
    if (
      !conta.id ||
      !conta.recorrencia ||
      conta.recorrencia === 'unica' ||
      !conta.is_ativa ||
      !conta.recorrencia_grupo_id ||
      !conta.proxima_execucao
    ) {
      continue;
    }

    let proximaExecucao = normalizarData(conta.proxima_execucao);
    let geradas = 0;

    while (proximaExecucao <= hoje && geradas < 12) {
      const chave = gerarChaveRecorrencia(conta, proximaExecucao);
      if (chave && !chavesExistentes.has(chave)) {
        const { data, error } = await contasPagarTable()
          .insert({
            ...gerarNovaContaRecorrente(conta, proximaExecucao),
            empresa_id: empresaId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();

        if (!error && data) {
          novasContas.push(data as unknown as ContaPagar);
          chavesExistentes.add(chave);
        } else if (error) {
          console.error('Erro ao gerar conta recorrente:', error);
        }
      }

      const proximaData = calcularProximaExecucao(proximaExecucao, conta.recorrencia);
      if (!proximaData) {
        break;
      }
      proximaExecucao = proximaData;
      geradas += 1;
    }

    if (geradas > 0) {
      await contasPagarTable()
        .update({
          proxima_execucao: proximaExecucao,
          updated_at: new Date().toISOString()
        })
        .eq('id', conta.id)
        .eq('empresa_id', empresaId);
    }
  }

  return [...contas, ...novasContas];
};

export async function listarContasPagar(filtros?: {
  status?: string;
  fornecedor_id?: string;
  data_inicio?: string;
  data_fim?: string;
}): Promise<ContaPagar[]> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return [];
    }

    let query = contasPagarTable()
      .select('*')
      .eq('empresa_id', empresaId)
      .order('data_vencimento', { ascending: true });

    if (filtros?.status) {
      query = query.eq('status', filtros.status);
    }

    if (filtros?.fornecedor_id) {
      query = query.eq('fornecedor_id', filtros.fornecedor_id);
    }

    // Não filtrar por data aqui - vamos buscar todas e filtrar depois
    // Isso garante que contas recebidas/pagas apareçam mesmo se data_recebimento/pagamento
    // estiver fora do período mas data_vencimento estiver dentro (ou vice-versa)

    const { data, error } = await query;

    if (error) {
      console.error('Erro ao listar contas a pagar:', error);
      return [];
    }

    // Atualizar status para 'atrasado' se necessário
    let contas = (data || []) as unknown as ContaPagar[];
    const hoje = getTodayAsISO();
    
    contas = contas.map(conta => {
      if (conta.status === 'pendente' && conta.data_vencimento < hoje) {
        return { ...conta, status: 'atrasado' as const };
      }
      return conta;
    });

    contas = await processarRecorrenciasContas(contas, empresaId);
    
    // Filtrar por data considerando tanto data_vencimento quanto data_pagamento
    if (filtros?.data_inicio || filtros?.data_fim) {
      contas = contas.filter(conta => {
        // Para contas pagas, usar data_pagamento se disponível, senão data_vencimento
        const dataRelevante = (conta.status === 'pago' && conta.data_pagamento) 
          ? conta.data_pagamento.split('T')[0] 
          : conta.data_vencimento.split('T')[0];
        
        const dentroPeriodo = (!filtros?.data_inicio || dataRelevante >= filtros.data_inicio) &&
                              (!filtros?.data_fim || dataRelevante <= filtros.data_fim);
        
        // Também incluir se a data_vencimento estiver no período (mesmo que pagamento não esteja)
        const vencimentoNoPeriodo = (!filtros?.data_inicio || conta.data_vencimento.split('T')[0] >= filtros.data_inicio) &&
                                     (!filtros?.data_fim || conta.data_vencimento.split('T')[0] <= filtros.data_fim);
        
        return dentroPeriodo || vencimentoNoPeriodo;
      });
    }
    
    return contas;
  } catch (error) {
    console.error('Erro ao listar contas a pagar:', error);
    return [];
  }
}

export async function criarContaPagar(conta: Omit<ContaPagar, 'id' | 'created_at' | 'updated_at' | 'empresa_id' | 'valor_pago'>): Promise<{ ok: boolean; data?: ContaPagar; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { ok: false, error: 'Usuário não tem empresa associada' };
    }

    // Gerar recorrencia_grupo_id se for recorrente
    const recorrenciaGrupoId = (conta.recorrencia && conta.recorrencia !== 'unica')
      ? `recorrencia-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      : null;

    // Calcular próxima execução se for recorrente
    const proximaExecucao = (conta.recorrencia && conta.recorrencia !== 'unica' && conta.data_vencimento)
      ? calcularProximaExecucao(conta.data_vencimento, conta.recorrencia as RecorrenciaContaPagar)
      : null;

    const { data, error } = await contasPagarTable()
      .insert({
        ...conta,
        empresa_id: empresaId,
        valor_pago: 0,
        status: conta.status || 'pendente',
        recorrencia: conta.recorrencia || 'unica',
        is_ativa: conta.is_ativa !== undefined ? conta.is_ativa : true,
        proxima_execucao: proximaExecucao,
        recorrencia_grupo_id: recorrenciaGrupoId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) {
      console.error('Erro ao criar conta a pagar:', error);
      return { ok: false, error: error.message };
    }

    // Processar recorrências para gerar as próximas execuções
    if (recorrenciaGrupoId && proximaExecucao) {
      const { data: contasRecorrentes } = await contasPagarTable()
        .select('*')
        .eq('empresa_id', empresaId)
        .eq('recorrencia_grupo_id', recorrenciaGrupoId);

      if (contasRecorrentes && contasRecorrentes.length > 0) {
        await processarRecorrenciasContas(contasRecorrentes as ContaPagar[], empresaId);
      }
    }

  return { ok: true, data: data as unknown as ContaPagar };
  } catch (error: unknown) {
    console.error('Erro ao criar conta a pagar:', error);
    const message = error instanceof Error ? error.message : 'Erro ao criar conta a pagar';
    return { ok: false, error: message };
  }
}

export async function atualizarContaPagar(id: string, conta: Partial<ContaPagar>): Promise<{ ok: boolean; data?: ContaPagar; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { ok: false, error: 'Usuário não tem empresa associada' };
    }

    // Atualizar status baseado no pagamento
  const updates: Partial<ContaPagar> & { updated_at: string } = { ...conta, updated_at: new Date().toISOString() };
    
    if (conta.valor_pago !== undefined && conta.valor_total !== undefined) {
      if (conta.valor_pago >= conta.valor_total) {
        updates.status = 'pago';
        updates.data_pagamento = conta.data_pagamento || getTodayAsISO();
      } else if (conta.valor_pago > 0) {
        updates.status = 'pendente';
      }
    }

    const { data, error } = await contasPagarTable()
      .update(updates)
      .eq('id', id)
      .eq('empresa_id', empresaId)
      .select()
      .single();

    if (error) {
      console.error('Erro ao atualizar conta a pagar:', error);
      return { ok: false, error: error.message };
    }

  return { ok: true, data: data as unknown as ContaPagar };
  } catch (error: unknown) {
    console.error('Erro ao atualizar conta a pagar:', error);
    const message = error instanceof Error ? error.message : 'Erro ao atualizar conta a pagar';
    return { ok: false, error: message };
  }
}

export async function deletarContaPagar(id: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { ok: false, error: 'Usuário não tem empresa associada' };
    }

    const { error } = await contasPagarTable()
      .delete()
      .eq('id', id)
      .eq('empresa_id', empresaId);

    if (error) {
      console.error('Erro ao deletar conta a pagar:', error);
      return { ok: false, error: error.message };
    }

    return { ok: true };
  } catch (error: unknown) {
    console.error('Erro ao deletar conta a pagar:', error);
    const message = error instanceof Error ? error.message : 'Erro ao deletar conta a pagar';
    return { ok: false, error: message };
  }
}

